/**
 * ヘルプトピック
 */
export interface HelpTopic {
  readonly id: string; // 一意識別子
  readonly title: string; // トピックタイトル
  readonly category: HelpCategory; // カテゴリ
  readonly markdownPath: string; // Markdownファイルパス
  readonly keywords: readonly string[]; // 検索キーワード
  readonly order: number; // 表示順序
  readonly requiresAuth?: boolean; // ログイン必須かどうか
  readonly roles?: readonly string[]; // 特定ロールのみアクセス可能
}

/**
 * ヘルプカテゴリ
 */
export enum HelpCategory {
  GETTING_STARTED = 'getting-started',
  EQUIPMENT = 'equipment',
  MAINTENANCE = 'maintenance',
  DOCUMENTS = 'documents',
  EMERGENCY = 'emergency',
  USER_MANAGEMENT = 'user-management',
  SETTINGS = 'settings',
  SUBSCRIPTION = 'subscription',
  FAQ = 'faq',
}

/**
 * ヘルプトピック一覧
 */
export const helpTopics: readonly HelpTopic[] = [
  // はじめに（公開）
  {
    id: 'getting-started-overview',
    title: 'サービス概要',
    category: HelpCategory.GETTING_STARTED,
    markdownPath: '/help/getting-started/overview.md',
    keywords: ['概要', 'サービス', 'はじめに', 'スタート', '一元帳'],
    order: 1,
    requiresAuth: false,
  },
  {
    id: 'getting-started-login',
    title: 'ログイン・登録方法',
    category: HelpCategory.GETTING_STARTED,
    markdownPath: '/help/getting-started/login.md',
    keywords: ['ログイン', '登録', 'サインアップ', 'パスワード', '認証'],
    order: 2,
    requiresAuth: false,
  },
  {
    id: 'getting-started-first-steps',
    title: '初回セットアップ',
    category: HelpCategory.GETTING_STARTED,
    markdownPath: '/help/getting-started/first-steps.md',
    keywords: ['セットアップ', '初期設定', '初回', 'はじめて'],
    order: 3,
    requiresAuth: false,
  },
  {
    id: 'getting-started-dashboard',
    title: 'ダッシュボード',
    category: HelpCategory.GETTING_STARTED,
    markdownPath: '/help/getting-started/dashboard.md',
    keywords: ['ダッシュボード', '概要', '通知', 'ショートカット'],
    order: 4,
    requiresAuth: true,
  },

  // 設備管理（ログイン必須）
  {
    id: 'equipment-overview',
    title: '設備管理の概要',
    category: HelpCategory.EQUIPMENT,
    markdownPath: '/help/equipment/overview.md',
    keywords: ['設備', '概要', '管理', '設備管理'],
    order: 10,
    requiresAuth: true,
  },
  {
    id: 'equipment-register',
    title: '設備の登録',
    category: HelpCategory.EQUIPMENT,
    markdownPath: '/help/equipment/register.md',
    keywords: ['設備', '登録', '追加', '新規', '作成'],
    order: 11,
    requiresAuth: true,
  },
  {
    id: 'equipment-manage',
    title: '設備の編集・削除',
    category: HelpCategory.EQUIPMENT,
    markdownPath: '/help/equipment/manage.md',
    keywords: ['設備', '編集', '削除', '更新', '変更'],
    order: 12,
    requiresAuth: true,
  },
  {
    id: 'equipment-categories',
    title: '設備カテゴリ管理',
    category: HelpCategory.EQUIPMENT,
    markdownPath: '/help/equipment/categories.md',
    keywords: ['カテゴリ', '分類', '階層'],
    order: 13,
    requiresAuth: true,
  },
  {
    id: 'equipment-locations',
    title: 'エリア管理',
    category: HelpCategory.EQUIPMENT,
    markdownPath: '/help/equipment/locations.md',
    keywords: ['エリア', '場所', '位置'],
    order: 14,
    requiresAuth: true,
  },
  {
    id: 'equipment-deterioration',
    title: '健全度予測機能',
    category: HelpCategory.EQUIPMENT,
    markdownPath: '/help/equipment/deterioration.md',
    keywords: ['健全度', '予測', 'EUL', '寿命'],
    order: 15,
    requiresAuth: true,
  },
  {
    id: 'equipment-analysis-overview',
    title: '予測・分析',
    category: HelpCategory.EQUIPMENT,
    markdownPath: '/help/equipment/analysis-overview.md',
    keywords: ['予測', '分析', 'レポート', 'ダッシュボード'],
    order: 16,
    requiresAuth: true,
  },
  {
    id: 'equipment-ledger-overview',
    title: '台帳管理',
    category: HelpCategory.EQUIPMENT,
    markdownPath: '/help/equipment/ledger-overview.md',
    keywords: ['台帳', '管理', '設備', '書類', '業者', '部品'],
    order: 17,
    requiresAuth: true,
  },
  {
    id: 'ledger-equipments',
    title: '設備',
    category: HelpCategory.EQUIPMENT,
    markdownPath: '/help/equipment/equipments-menu.md',
    keywords: ['設備', '台帳', '一覧', '管理'],
    order: 18,
    requiresAuth: true,
  },
  {
    id: 'ledger-locations',
    title: 'エリア',
    category: HelpCategory.EQUIPMENT,
    markdownPath: '/help/equipment/locations-menu.md',
    keywords: ['エリア', '場所', '台帳', '拠点'],
    order: 19,
    requiresAuth: true,
  },

  // メンテナンス（ログイン必須）
  {
    id: 'maintenance-overview',
    title: 'メンテナンス管理の概要',
    category: HelpCategory.MAINTENANCE,
    markdownPath: '/help/maintenance/overview.md',
    keywords: ['メンテナンス', '概要', '保守'],
    order: 20,
    requiresAuth: true,
  },
  {
    id: 'maintenance-schedule-menu',
    title: 'スケジュール',
    category: HelpCategory.MAINTENANCE,
    markdownPath: '/help/maintenance/schedule-menu.md',
    keywords: ['スケジュール', '予定', '作業', '履歴', '緊急'],
    order: 21,
    requiresAuth: true,
  },
  {
    id: 'maintenance-calendar',
    title: '保全カレンダー表示',
    category: HelpCategory.MAINTENANCE,
    markdownPath: '/help/maintenance/calendar.md',
    keywords: ['保全カレンダー', '予定', 'スケジュール'],
    order: 22,
    requiresAuth: true,
  },
  {
    id: 'maintenance-schedule',
    title: '予定の作成・編集',
    category: HelpCategory.MAINTENANCE,
    markdownPath: '/help/maintenance/schedule.md',
    keywords: ['予定', '作成', '編集', 'スケジュール'],
    order: 23,
    requiresAuth: true,
  },
  {
    id: 'maintenance-work-order',
    title: '作業指示書',
    category: HelpCategory.MAINTENANCE,
    markdownPath: '/help/maintenance/work-order.md',
    keywords: ['作業指示書', '指示', '作業'],
    order: 24,
    requiresAuth: true,
  },
  {
    id: 'maintenance-work-history',
    title: '作業履歴の確認',
    category: HelpCategory.MAINTENANCE,
    markdownPath: '/help/maintenance/work-history.md',
    keywords: ['履歴', '作業履歴', '過去', '実績'],
    order: 25,
    requiresAuth: true,
  },
  {
    id: 'maintenance-parts-management',
    title: '部品・消耗品・資材管理',
    category: HelpCategory.MAINTENANCE,
    markdownPath: '/help/maintenance/parts-management.md',
    keywords: ['部品・消耗品', '資材', '在庫', '仕入先'],
    order: 26,
    requiresAuth: true,
  },
  {
    id: 'maintenance-my-tasks',
    title: 'マイタスク',
    category: HelpCategory.MAINTENANCE,
    markdownPath: '/help/maintenance/my-tasks.md',
    keywords: ['マイタスク', 'タスク', '担当', '承認', '作業', '自分の作業'],
    order: 27,
    requiresAuth: true,
  },
  {
    id: 'maintenance-inventory-overview',
    title: '部品・消耗品在庫管理',
    category: HelpCategory.MAINTENANCE,
    markdownPath: '/help/maintenance/inventory-overview.md',
    keywords: ['在庫', '部品・消耗品', '発注', '消費', '分析'],
    order: 28,
    requiresAuth: true,
  },
  {
    id: 'maintenance-stock-dashboard',
    title: '在庫ダッシュボード',
    category: HelpCategory.MAINTENANCE,
    markdownPath: '/help/maintenance/stock-dashboard.md',
    keywords: ['在庫', 'ダッシュボード', '不足', 'アラート'],
    order: 29,
    requiresAuth: true,
  },
  {
    id: 'maintenance-purchase-orders',
    title: '発注管理',
    category: HelpCategory.MAINTENANCE,
    markdownPath: '/help/maintenance/purchase-orders.md',
    keywords: ['発注', '納品', '履歴', '管理'],
    order: 30,
    requiresAuth: true,
  },
  {
    id: 'maintenance-part-consumption-logs',
    title: '消費管理',
    category: HelpCategory.MAINTENANCE,
    markdownPath: '/help/maintenance/part-consumption-logs.md',
    keywords: ['消費', '記録', '履歴', '部品・消耗品'],
    order: 31,
    requiresAuth: true,
  },
  {
    id: 'maintenance-parts-analytics',
    title: '部品・消耗品分析',
    category: HelpCategory.MAINTENANCE,
    markdownPath: '/help/maintenance/parts-analytics.md',
    keywords: ['分析', 'コスト', '予算', '部品・消耗品'],
    order: 32,
    requiresAuth: true,
  },
  {
    id: 'ledger-parts',
    title: '部品・消耗品',
    category: HelpCategory.MAINTENANCE,
    markdownPath: '/help/maintenance/parts-menu.md',
    keywords: ['部品・消耗品', '台帳', '在庫', '管理'],
    order: 33,
    requiresAuth: true,
  },
  {
    id: 'ledger-suppliers',
    title: '業者',
    category: HelpCategory.MAINTENANCE,
    markdownPath: '/help/maintenance/suppliers-menu.md',
    keywords: ['業者', '仕入先', '連絡先', '台帳'],
    order: 34,
    requiresAuth: true,
  },

  // 書類（ログイン必須）
  {
    id: 'ledger-documents',
    title: '書類',
    category: HelpCategory.DOCUMENTS,
    markdownPath: '/help/documents/documents-menu.md',
    keywords: ['書類', '台帳', '管理', '一覧'],
    order: 59,
    requiresAuth: true,
  },
  {
    id: 'documents-overview',
    title: '書類管理の概要',
    category: HelpCategory.DOCUMENTS,
    markdownPath: '/help/documents/overview.md',
    keywords: ['書類', '台帳', '概要', '管理', 'ファイル'],
    order: 60,
    requiresAuth: true,
  },
  {
    id: 'documents-register',
    title: '書類の登録',
    category: HelpCategory.DOCUMENTS,
    markdownPath: '/help/documents/register.md',
    keywords: ['書類', '登録', '追加', 'アップロード', 'ファイル'],
    order: 61,
    requiresAuth: true,
  },
  {
    id: 'documents-manage',
    title: '書類の詳細・改版',
    category: HelpCategory.DOCUMENTS,
    markdownPath: '/help/documents/manage.md',
    keywords: ['書類', '詳細', '改版', 'バージョン', '履歴', '編集'],
    order: 62,
    requiresAuth: true,
  },
  {
    id: 'documents-official-inspections',
    title: '法定点検ビュー',
    category: HelpCategory.DOCUMENTS,
    markdownPath: '/help/documents/official-inspections.md',
    keywords: ['法定点検', '提出', '承認', '差戻し', '提出履歴'],
    order: 63,
    requiresAuth: true,
  },
  {
    id: 'documents-categories',
    title: '書類カテゴリ管理',
    category: HelpCategory.DOCUMENTS,
    markdownPath: '/help/documents/categories.md',
    keywords: ['書類カテゴリ', 'カテゴリ', '有効化', '区分', '関連タイプ'],
    order: 64,
    requiresAuth: true,
  },

  // 緊急作業（ログイン必須）
  {
    id: 'emergency-overview',
    title: '緊急作業管理の概要',
    category: HelpCategory.EMERGENCY,
    markdownPath: '/help/emergency/overview.md',
    keywords: ['緊急', '概要', 'アラート'],
    order: 30,
    requiresAuth: true,
  },
  {
    id: 'emergency-create-work',
    title: '緊急作業の登録',
    category: HelpCategory.EMERGENCY,
    markdownPath: '/help/emergency/create-work.md',
    keywords: ['緊急', '登録', '作成', 'アラート'],
    order: 31,
    requiresAuth: true,
  },
  {
    id: 'emergency-manage-work',
    title: '緊急作業の管理',
    category: HelpCategory.EMERGENCY,
    markdownPath: '/help/emergency/manage-work.md',
    keywords: ['緊急', '管理', 'ステータス', '完了'],
    order: 32,
    requiresAuth: true,
  },

  // ユーザー管理（ログイン必須）
  {
    id: 'user-management-overview',
    title: 'ユーザー管理の概要',
    category: HelpCategory.USER_MANAGEMENT,
    markdownPath: '/help/user-management/overview.md',
    keywords: ['ユーザー', '概要', '管理'],
    order: 40,
    requiresAuth: true,
  },
  {
    id: 'user-management-invite',
    title: 'ユーザーの招待',
    category: HelpCategory.USER_MANAGEMENT,
    markdownPath: '/help/user-management/invite.md',
    keywords: ['招待', 'ユーザー', '追加', 'メール'],
    order: 41,
    requiresAuth: true,
  },
  {
    id: 'user-management-roles',
    title: '権限とロール',
    category: HelpCategory.USER_MANAGEMENT,
    markdownPath: '/help/user-management/roles.md',
    keywords: ['権限', 'ロール', '役割', '管理者'],
    order: 42,
    requiresAuth: true,
  },
  {
    id: 'user-management-profile',
    title: 'プロフィール設定',
    category: HelpCategory.USER_MANAGEMENT,
    markdownPath: '/help/user-management/profile.md',
    keywords: ['プロフィール', '設定', 'アカウント', 'パスワード'],
    order: 43,
    requiresAuth: true,
  },

  // 会社設定（ログイン必須）
  {
    id: 'settings-company',
    title: '会社情報',
    category: HelpCategory.SETTINGS,
    markdownPath: '/help/settings/company.md',
    keywords: ['会社情報', '基本情報', '所在地', '連絡先'],
    order: 70,
    requiresAuth: true,
  },
  {
    id: 'settings-branches',
    title: 'マップ',
    category: HelpCategory.SETTINGS,
    markdownPath: '/help/settings/branches.md',
    keywords: ['マップ', '拠点', '工場', '所在地', 'エリア'],
    order: 71,
    requiresAuth: true,
  },
  {
    id: 'settings-advanced',
    title: '詳細設定',
    category: HelpCategory.SETTINGS,
    markdownPath: '/help/settings/advanced.md',
    keywords: ['詳細設定', '高度', 'オプション', '設定'],
    order: 72,
    requiresAuth: true,
  },
  {
    id: 'settings-system-config',
    title: 'システム設定',
    category: HelpCategory.SETTINGS,
    markdownPath: '/help/settings/system-config.md',
    keywords: ['システム設定', '運用', '権限', '通知'],
    order: 73,
    requiresAuth: true,
  },

  // サブスクリプション（ログイン必須）
  {
    id: 'subscription-overview',
    title: 'プラン概要',
    category: HelpCategory.SUBSCRIPTION,
    markdownPath: '/help/subscription/overview.md',
    keywords: ['プラン', '概要', 'サブスクリプション'],
    order: 50,
    requiresAuth: true,
  },
  {
    id: 'subscription-plans',
    title: 'プランの種類と機能',
    category: HelpCategory.SUBSCRIPTION,
    markdownPath: '/help/subscription/plans.md',
    keywords: ['プラン', '種類', '機能', '料金'],
    order: 51,
    requiresAuth: true,
  },
  {
    id: 'subscription-payment',
    title: '支払い方法の設定',
    category: HelpCategory.SUBSCRIPTION,
    markdownPath: '/help/subscription/payment.md',
    keywords: ['支払い', 'クレジットカード', '決済', '請求'],
    order: 52,
    requiresAuth: true,
  },
  {
    id: 'subscription-upgrade',
    title: 'プランのアップグレード',
    category: HelpCategory.SUBSCRIPTION,
    markdownPath: '/help/subscription/upgrade.md',
    keywords: ['アップグレード', 'プラン変更', '変更'],
    order: 53,
    requiresAuth: true,
  },
  {
    id: 'subscription-cancel',
    title: 'プランのキャンセル',
    category: HelpCategory.SUBSCRIPTION,
    markdownPath: '/help/subscription/cancel.md',
    keywords: ['キャンセル', '解約', '終了'],
    order: 54,
    requiresAuth: true,
  },

  // 通知（ログイン必須）
  {
    id: 'notifications-inbox',
    title: '受信通知',
    category: HelpCategory.SETTINGS,
    markdownPath: '/help/notifications/inbox.md',
    keywords: ['通知', '受信', '未読', '既読', '非表示', '受信箱'],
    order: 74,
    requiresAuth: true,
  },

  // よくある質問（公開）
  {
    id: 'faq-general',
    title: 'よくある質問',
    category: HelpCategory.FAQ,
    markdownPath: '/help/faq/general.md',
    keywords: ['FAQ', '質問', 'よくある', 'Q&A'],
    order: 90,
    requiresAuth: false,
  },
  {
    id: 'faq-troubleshooting',
    title: 'トラブルシューティング',
    category: HelpCategory.FAQ,
    markdownPath: '/help/faq/troubleshooting.md',
    keywords: ['トラブル', 'エラー', '問題', '解決'],
    order: 91,
    requiresAuth: false,
  },
  {
    id: 'faq-contact',
    title: 'お問い合わせ',
    category: HelpCategory.FAQ,
    markdownPath: '/help/faq/contact.md',
    keywords: ['問い合わせ', 'サポート', '連絡', 'ヘルプ'],
    order: 92,
    requiresAuth: false,
  },
] as const;

/**
 * カテゴリ情報
 */
export const helpCategories = {
  [HelpCategory.GETTING_STARTED]: {
    label: 'はじめに',
    icon: '🚀',
    description: 'アプリの基本的な使い方',
  },
  [HelpCategory.EQUIPMENT]: {
    label: '設備管理',
    icon: '⚙️',
    description: '設備の登録・管理・健全度予測',
  },
  [HelpCategory.MAINTENANCE]: {
    label: 'メンテナンス',
    icon: '🔧',
    description: 'メンテナンス予定と作業履歴',
  },
  [HelpCategory.DOCUMENTS]: {
    label: '書類',
    icon: '📄',
    description: '書類の登録・改版・提出管理',
  },
  [HelpCategory.EMERGENCY]: {
    label: '緊急作業',
    icon: '🚨',
    description: '緊急作業の管理',
  },
  [HelpCategory.USER_MANAGEMENT]: {
    label: 'ユーザー管理',
    icon: '👥',
    description: 'ユーザーの招待と権限管理',
  },
  [HelpCategory.SETTINGS]: {
    label: '会社設定',
    icon: '??',
    description: '会社情報や運用設定',
  },
  [HelpCategory.SUBSCRIPTION]: {
    label: '契約プラン',
    icon: '💳',
    description: 'プランと決済管理',
  },
  [HelpCategory.FAQ]: {
    label: 'よくある質問',
    icon: '❓',
    description: 'よくある質問とトラブルシューティング',
  },
} as const;

/**
 * IDからトピックを取得
 */
export function getTopicById(id: string): HelpTopic | undefined {
  return helpTopics.find((topic) => topic.id === id);
}

/**
 * カテゴリからトピック一覧を取得
 */
export function getTopicsByCategory(category: HelpCategory): readonly HelpTopic[] {
  return helpTopics.filter((topic) => topic.category === category);
}
